import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';

import { ProfileImage } from '../../../assets';
import { getSocket } from '../../../services/socket';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import {
	tournamentsRegisterErrorClear,
	tournamentsRegisterRequest,
	tournamentsUnregisterRequest,
} from '../../../store/tournaments/tournamentsActions';
import { selectTournaments } from '../../../store/tournaments/tournamentsReducer';
import { RootStackParamList } from '../../navigator';
import type { SnG } from '../Middle/TournamentsSection';

export type UseTournamentRegistrationParams = {
	tournamentId: string | undefined;
	roomId: string | undefined;
	/** Optional. If not provided, sng is resolved from Redux by tournamentId. */
	sng?: SnG | null;
};

export type SignedPlayer = {
	id: string | number;
	name: string;
	score: number;
	chips: any;
	isVip: boolean;
	avatarImage: { uri: string } | number;
};

type Nav = NativeStackNavigationProp<RootStackParamList>;

export function useTournamentRegistration({
	tournamentId,
	roomId,
	sng: sngProp,
}: UseTournamentRegistrationParams) {
	const dispatch = useDispatch();
	const { navigate } = useNavigation<Nav>();
	const { t } = useTranslation();
	const playerId = useSelector((state: RootState) => state.auth.user?.id);
	const playerNumericId = useSelector((state: RootState) => state.auth.user?.numericId);
	const playerLevel = Number(useSelector((state: RootState) => state.auth.user?.level) ?? 0);
	const username = useSelector((state: RootState) => state.auth.user?.username);
	const userIsVip = useSelector((state: RootState) => state.auth.user?.isVip);
	const socketPlayerId = String(playerId ?? 'anonymous');
	const socketUsername = String(username ?? 'anonymous');
	const tournaments = useSelector(selectTournaments);
	const registerLoading = useSelector((state: RootState) => state.tournaments.registerLoading);
	const registerError = useSelector((state: RootState) => state.tournaments.registerError);
	const registerErrorTournamentId = useSelector(
		(state: RootState) => state.tournaments.registerErrorTournamentId,
	);
	const isLogged = useSelector(selectIsLogged);

	const storeSng = useMemo(
		() => tournaments.find(item => String(item.id) === String(tournamentId)),
		[tournaments, tournamentId],
	);

	const sng: SnG | undefined = useMemo(() => {
		if (storeSng && sngProp) return { ...sngProp, ...storeSng };
		return storeSng ?? sngProp ?? undefined;
	}, [storeSng, sngProp]);

	const [players, setPlayers] = useState<any[]>([]);
	const [details, setDetails] = useState<any>(null);
	const [optimisticIsSigned, setOptimisticIsSigned] = useState<boolean | null>(null);
	const [registerActionPending, setRegisterActionPending] = useState(false);
	const refetchLastSentRef = React.useRef<Record<string, number>>({});
	const registerActionTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const tournamentChips = useMemo(() => {
		const value = details?.tournament_chips ?? sng?.tournament_chips ?? 0;
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	}, [details?.tournament_chips, sng?.tournament_chips]);

	const signedPlayers = useMemo((): SignedPlayer[] => {
		const source = players.length
			? players
			: Array.isArray((sng as any)?.players)
				? (sng as any).players
				: [];

		return source.map((p: any) => {
			const listedPlayerId = p.id ?? p.user_id ?? p.player_id;
			const isCurrentPlayer =
				listedPlayerId != null &&
				[String(playerId), String(playerNumericId)].includes(String(listedPlayerId));

			return {
				id: p.id,
				name: p.username,
				score: p.score ?? 0,
				// Registered-player payloads can include wallet chips; prefer tournament stack values.
				chips: (p.tournament_chips ?? p.tournamentChips ?? tournamentChips) || Number(p.chips ?? 0),
				isVip: Boolean(p.isVip ?? p.is_vip ?? p.is_vip_user ?? (isCurrentPlayer && userIsVip)),
				avatarImage: p.avatar ? { uri: p.avatar } : ProfileImage,
			};
		});
	}, [userIsVip, playerId, playerNumericId, players, sng, tournamentChips]);

	const membershipSource = useMemo(() => {
		const candidates = [
			players,
			(details as any)?.players,
			(details as any)?.users,
			(sng as any)?.players,
			(sng as any)?.users,
		];

		return candidates.find(Array.isArray) ?? [];
	}, [players, details, sng]);

	const signedUserIds = useMemo(() => {
		return membershipSource
			.map((entry: any) => {
				if (entry == null) return null;
				if (typeof entry === 'object') return entry.id ?? entry.user_id ?? entry.player_id ?? null;
				return entry;
			})
			.filter((id): id is string | number => id != null)
			.map(String);
	}, [membershipSource]);

	const serverIsSigned = useMemo(() => {
		if (!playerId) return false;
		if (signedUserIds.includes(String(playerId))) return true;
		if (signedPlayers.some(p => String(p.id) === String(playerId))) return true;
		if (sng?.registered === false) return false;
		return sng?.registered === true;
	}, [signedPlayers, signedUserIds, playerId, sng?.registered]);

	const isSigned = useMemo(() => {
		if (!playerId) return false;
		return optimisticIsSigned ?? serverIsSigned;
	}, [optimisticIsSigned, playerId, serverIsSigned]);

	useEffect(() => {
		if (optimisticIsSigned == null) return;
		if (optimisticIsSigned === serverIsSigned) {
			setOptimisticIsSigned(null);
		}
	}, [optimisticIsSigned, serverIsSigned]);

	useEffect(() => {
		return () => {
			if (registerActionTimeoutRef.current) {
				clearTimeout(registerActionTimeoutRef.current);
			}
		};
	}, []);

	React.useEffect(() => {
		console.log('[TOURNAMENT] isSigned state', {
			tournamentId,
			isSigned,
			playerId,
			signedCount: signedPlayers.length,
		});
	}, [tournamentId, isSigned, playerId, signedPlayers.length]);

	const scopedRegisterError = useMemo(() => {
		if (!registerError || !tournamentId) return null;
		return registerErrorTournamentId === tournamentId ? registerError : null;
	}, [registerError, registerErrorTournamentId, tournamentId]);

	const requiredLevel = 10;
	const isLevelLocked = !isSigned && requiredLevel > 0 && playerLevel < requiredLevel;

	const serverRequiredLevel = useMemo(() => {
		if (!scopedRegisterError) return null;
		const match = String(scopedRegisterError).match(/level\s+(\d+)/i);
		if (!match?.[1]) return null;
		const parsed = Number(match[1]);
		return Number.isFinite(parsed) ? parsed : null;
	}, [scopedRegisterError]);

	const hasServerLevelError = useMemo(() => {
		if (!scopedRegisterError) return false;
		return /at least level/i.test(String(scopedRegisterError));
	}, [scopedRegisterError]);

	const hasInsufficientBalanceError = useMemo(() => {
		if (!scopedRegisterError) return false;
		return /insufficient balance/i.test(String(scopedRegisterError));
	}, [scopedRegisterError]);

	const registrationFeedback = useMemo(() => {
		const shownRequiredLevel = serverRequiredLevel ?? requiredLevel;
		const showLevelError = isLogged && (isLevelLocked || (!isSigned && hasServerLevelError));
		const showBalanceError = isLogged && !isSigned && hasInsufficientBalanceError;

		return {
			shownRequiredLevel,
			showLevelError,
			showBalanceError,
		};
	}, [
		serverRequiredLevel,
		requiredLevel,
		isLogged,
		isLevelLocked,
		isSigned,
		hasServerLevelError,
		hasInsufficientBalanceError,
	]);

	useEffect(() => {
		if (!scopedRegisterError) return;
		setOptimisticIsSigned(null);

		const clearId = setTimeout(() => {
			dispatch(tournamentsRegisterErrorClear());
		}, 5000);

		return () => clearTimeout(clearId);
	}, [scopedRegisterError, dispatch]);

	// Socket listener for getSnGTournamentDetails
	useEffect(() => {
		if (!tournamentId || !roomId) return;

		const socket = getSocket(socketPlayerId, socketUsername);

		console.log('[TOURNAMENT] Registering getSnGTournamentDetails listener', {
			tournamentId,
			roomId,
			socketPlayerId,
		});

		const handler = (packet: any) => {
			const data = packet?.data ?? packet;

			const responseTournamentId = data?.id != null ? String(data.id) : null;
			if (responseTournamentId && responseTournamentId !== String(tournamentId)) {
				return;
			}

			console.log('[TOURNAMENT] getSnGTournamentDetails received', {
				tournamentId,
				roomId,
				playerCount: Array.isArray(data.players) ? data.players.length : 0,
				hasDetails: !!data,
			});
			setDetails(data);
			if (Array.isArray(data.players)) {
				setPlayers(data.players);
			} else if (Array.isArray(data.users)) {
				setPlayers(data.users);
			}
		};

		socket.on('getSnGTournamentDetails', handler);
		return () => {
			console.log('[TOURNAMENT] Removing getSnGTournamentDetails listener', {
				tournamentId,
				roomId,
			});
			socket.off('getSnGTournamentDetails', handler);
		};
	}, [socketPlayerId, socketUsername, tournamentId, roomId]);

	// Initial fetch and explicit refreshes
	const refetch = React.useCallback(
		(_reason: string) => {
			if (!tournamentId || !roomId) return;

			const key = `${tournamentId}:${roomId}`;
			const now = Date.now();
			const lastSentAt = refetchLastSentRef.current[key] ?? 0;
			const minIntervalMs = 1000;
			if (now - lastSentAt < minIntervalMs) {
				console.log('[TOURNAMENT] Refetch throttled', { tournamentId, roomId, reason: _reason });
				return;
			}
			refetchLastSentRef.current[key] = now;

			console.log('[TOURNAMENT] Refetching details', { tournamentId, roomId, reason: _reason });
			const socket = getSocket(socketPlayerId, socketUsername);
			socket.emit('tournament:getSnGTournamentDetails', {
				tournament_id: tournamentId,
				room: roomId,
			});
		},
		[socketPlayerId, socketUsername, tournamentId, roomId],
	);

	useEffect(() => {
		if (!tournamentId || !roomId) return;
		refetch('initial');
	}, [tournamentId, roomId, refetch]);

	// Refetch after register/unregister completes
	const prevLoadingRef = React.useRef<boolean>(false);
	const prevRegisteredRef = React.useRef<boolean | undefined>(sng?.registered);

	useEffect(() => {
		if (!tournamentId || !roomId) return;

		const currentRegistered = sng?.registered;
		const prevRegistered = prevRegisteredRef.current;

		const loadingCompleted = prevLoadingRef.current === true && registerLoading === false;
		const registeredStatusChanged =
			prevRegistered !== undefined && currentRegistered !== prevRegistered;

		if (loadingCompleted || registeredStatusChanged) {
			refetch('register-state-change');
		}

		prevLoadingRef.current = registerLoading;
		prevRegisteredRef.current = currentRegistered;
	}, [tournamentId, roomId, registerLoading, sng?.registered, refetch]);

	const startRegisterActionCooldown = React.useCallback(() => {
		setRegisterActionPending(true);
		if (registerActionTimeoutRef.current) {
			clearTimeout(registerActionTimeoutRef.current);
		}
		registerActionTimeoutRef.current = setTimeout(() => {
			setRegisterActionPending(false);
			registerActionTimeoutRef.current = null;
		}, 1200);
	}, []);

	const handleRegisterPress = React.useCallback(() => {
		if (!tournamentId) {
			console.log('[TOURNAMENT] handleRegisterPress: no tournamentId');
			return;
		}

		if (registerActionPending) {
			console.log('[TOURNAMENT] handleRegisterPress: request already pending', { tournamentId });
			return;
		}

		if (!playerId) {
			console.log('[TOURNAMENT] handleRegisterPress: no playerId, navigating to Login');
			navigate('Login');
			return;
		}

		if (!isSigned && requiredLevel > 0 && playerLevel < requiredLevel) {
			console.log('[TOURNAMENT] handleRegisterPress: level locked', { requiredLevel, playerLevel });
			return;
		}

		console.log('[TOURNAMENT] handleRegisterPress', {
			tournamentId,
			isSigned,
			action: isSigned ? 'unregister' : 'register',
		});

		if (isSigned) {
			startRegisterActionCooldown();
			setOptimisticIsSigned(false);
			dispatch(tournamentsUnregisterRequest({ tournamentId }));

			Toast.show({
				type: 'success',
				text1: t('T_TOURNAMENT_UNREGISTER'),
				visibilityTime: 3200,
				position: 'bottom',
			});

			return;
		}

		startRegisterActionCooldown();
		setOptimisticIsSigned(true);
		dispatch(tournamentsRegisterRequest({ tournamentId }));

		Toast.show({
			type: 'success',
			text1: t('T_TOURNAMENT_REGISTER'),
			visibilityTime: 3200,
			position: 'bottom',
		});
	}, [
		tournamentId,
		playerId,
		playerLevel,
		requiredLevel,
		isSigned,
		registerActionPending,
		startRegisterActionCooldown,
		dispatch,
		navigate,
		t,
	]);

	return {
		playerId,
		username,
		players,
		setPlayers,
		details,
		setDetails,
		signedPlayers,
		isSigned,
		registerActionPending,
		registerLoading,
		registerError: scopedRegisterError,
		sng,
		requiredLevel,
		playerLevel,
		isLevelLocked,
		serverRequiredLevel,
		hasServerLevelError,
		hasInsufficientBalanceError,
		registrationFeedback,
		handleRegisterPress,
		refetch,
	};
}
